// Platform administration of OEM Partners (documentation/oem-partner-architecture.md, phase 3 API).
// Platform administrators only; the server enforces it (route policy + oem_partners.manage).
import type { IssuedInvitation } from './identityPlatform';
import { apiRequest } from './tenancy';

export type OEMStatus = 'active' | 'disabled';

export interface OEMPartner {
  id: string;
  name: string;
  legal_name: string;
  contact_name: string;
  contact_email: string;
  contact_phone: string;
  support_email: string;
  support_phone: string;
  support_url: string;
  status: OEMStatus;
  disabled_at: string | null;
  created_at: string;
  updated_at: string;
  customer_count: number;
  admin_count: number;
}

/** Editable OEM metadata; on update only the fields sent change ("" clears an optional field). */
export interface OEMProfile {
  name?: string;
  legal_name?: string;
  contact_name?: string;
  contact_email?: string;
  contact_phone?: string;
  support_email?: string;
  support_phone?: string;
  support_url?: string;
}

export const OEM_PROFILE_FIELDS: { key: keyof OEMProfile; label: string; kind: 'text' | 'email' | 'tel' | 'url'; required?: boolean; max: number }[] = [
  { key: 'name', label: 'Name', kind: 'text', required: true, max: 255 },
  { key: 'legal_name', label: 'Legal name', kind: 'text', max: 255 },
  { key: 'contact_name', label: 'Contact name', kind: 'text', max: 255 },
  { key: 'contact_email', label: 'Contact e-mail', kind: 'email', max: 255 },
  { key: 'contact_phone', label: 'Contact phone', kind: 'tel', max: 50 },
  { key: 'support_email', label: 'Support e-mail', kind: 'email', max: 255 },
  { key: 'support_phone', label: 'Support phone', kind: 'tel', max: 50 },
  { key: 'support_url', label: 'Support URL', kind: 'url', max: 500 },
];

export interface OEMCustomer {
  id: string;
  name: string;
  created_at: string;
  network_count: number;
}

/** Derived state of a user's RadioOS-managed sign-in (never a secret; computed by the server). */
export type ManagedAccountState =
  | 'not_provisioned' | 'activation_pending' | 'invited' | 'mfa_required' | 'active' | 'locked' | 'disabled';

export interface ManagedAccountStatus {
  user_id: string;
  provisioned: boolean;
  login_email?: string;
  state: ManagedAccountState;
  password_set: boolean;
  activation: { pending: boolean; expires_at?: string };
  mfa: { required: boolean; enrolled: boolean };
  disabled: boolean;
  disabled_reason?: 'user' | 'oem';
  locked_until?: string;
  managed_auth_enabled?: boolean;
}

export interface OEMUser {
  id: string;
  username: string;
  email: string;
  disabled: boolean;
  is_oem_admin: boolean;
  /** A linked Microsoft/Google identity (a sign-in method like any other). */
  identity_linked: boolean;
  /** invited | active (unified identity). */
  status?: string;
  account: ManagedAccountStatus;
}

/**
 * A freshly issued activation / password-reset link: shown once, never retrievable again. A
 * password-reset link that was e-mailed (delivery_state "sent") is not in the response at all.
 */
export interface ActivationLink {
  activation_url?: string;
  expires_at: string;
  reissued: boolean;
  notice: string;
  delivery_state?: 'sent' | 'failed' | 'not_configured';
  delivery_category?: string;
}

export interface OEMRoleAssignment {
  id: string;
  user_id: string;
  username: string;
  role: string;
  created_at: string;
}

const json = (method: string, body: unknown): RequestInit => ({ method, body: JSON.stringify(body) });
const base = '/api/admin/oem-partners';
const seg = encodeURIComponent;

export const listOEMPartners = () => apiRequest<OEMPartner[]>(base);
export const getOEMPartner = (id: string) => apiRequest<OEMPartner>(`${base}/${seg(id)}`);
export const createOEMPartner = (profile: OEMProfile) => apiRequest<{ id: string }>(base, json('POST', profile));
export const updateOEMPartner = (id: string, profile: OEMProfile) => apiRequest<void>(`${base}/${seg(id)}`, json('PUT', profile));
export const setOEMPartnerEnabled = (id: string, enabled: boolean) =>
  apiRequest<void>(`${base}/${seg(id)}/${enabled ? 'enable' : 'disable'}`, { method: 'POST' });
export const listOEMCustomers = (id: string) => apiRequest<OEMCustomer[]>(`${base}/${seg(id)}/customers`);
export const listOEMUsers = (id: string) => apiRequest<OEMUser[]>(`${base}/${seg(id)}/users`);
/** Invites a person into the OEM (oem_user, or oem_admin); they accept with Microsoft, Google or a RadioOS password. */
export const createOEMUser = (id: string, name: string, email: string, admin = false) =>
  apiRequest<IssuedInvitation & { id: string }>(`${base}/${seg(id)}/users`, json('POST', { display_name: name, email, is_oem_admin: admin }));
export const listOEMRoleAssignments = (id: string) => apiRequest<OEMRoleAssignment[]>(`${base}/${seg(id)}/role-assignments`);
export const grantOEMAdmin = (id: string, userId: string) =>
  apiRequest<{ id: string }>(`${base}/${seg(id)}/role-assignments`, json('POST', { user_id: userId }));
export const revokeOEMAdmin = (id: string, assignmentId: string) =>
  apiRequest<void>(`${base}/${seg(id)}/role-assignments/${seg(assignmentId)}`, { method: 'DELETE' });

// Administrative lifecycle (documentation/administrative-lifecycle.md). The previews compute the same
// impact as the operations; they require a platform administrator's strong (MFA) session.
export interface NamedRef { id: string; name: string }
export interface DeletedUser {
  id: string;
  username: string;
  login_email?: string;
  roles: string[];
  managed_account: boolean;
  active_sessions: number;
}
export interface OEMDeleteImpact {
  oem_partner: NamedRef;
  users: DeletedUser[];
  administrators: number;
  managed_accounts: number;
  active_sessions: number;
  /** People without any other access: disabled (kept, never deleted). */
  users_disabled?: NamedRef[];
  /** Customers stay unchanged; they only become direct RadioOS customers. */
  customers_detached: NamedRef[];
}
export const previewOEMPartnerDelete = (id: string) => apiRequest<OEMDeleteImpact>(`${base}/${seg(id)}/delete-preview`);
export const deleteOEMPartner = (id: string) =>
  apiRequest<{ status: string; impact: OEMDeleteImpact }>(`${base}/${seg(id)}`, { method: 'DELETE' });
export const deleteOEMUser = (id: string, userId: string) =>
  apiRequest<{ status: string; user: DeletedUser }>(`${base}/${seg(id)}/users/${seg(userId)}`, { method: 'DELETE' });

// RadioOS-managed accounts (platform administrators only). The administrator never sets a
// password: the user chooses it through the one-time activation link.
const managed = '/api/admin/managed-accounts';
export const getManagedAccount = (userId: string) => apiRequest<ManagedAccountStatus>(`${managed}/${seg(userId)}`);
export const provisionManagedAccount = (userId: string, loginEmail: string) =>
  apiRequest<ManagedAccountStatus>(`${managed}/${seg(userId)}`, json('POST', { login_email: loginEmail }));
/** Issues (or re-issues, invalidating the previous one) the one-time activation link. */
export const issueActivation = (userId: string) =>
  apiRequest<ActivationLink>(`${managed}/${seg(userId)}/activation`, { method: 'POST' });
/**
 * Account recovery (Microsoft Entra platform administrators only). A password-reset link lets the
 * user choose a new password (the authenticator stays); an MFA reset removes the authenticator and
 * every recovery code and ends all sessions. Neither returns a password or a factor.
 */
export const issuePasswordReset = (userId: string) =>
  apiRequest<ActivationLink>(`${managed}/${seg(userId)}/password-reset`, { method: 'POST' });
export const resetMFA = (userId: string) =>
  apiRequest<{ authenticator_removed: boolean; recovery_codes_removed: number; sessions_revoked: number }>(`${managed}/${seg(userId)}/mfa-reset`, { method: 'POST' });

/**
 * Attach a customer to an OEM Partner, transfer it, or detach it to direct RadioOS (oemId null).
 * Only the delegated-administration relationship changes on the server.
 */
export const setCustomerOEM = (customerId: string, oemId: string | null) =>
  apiRequest<{ result: 'attached' | 'transferred' | 'detached' | 'unchanged' }>(`/api/admin/tenants/${seg(customerId)}/oem-partner`, json('PUT', { oem_partner_id: oemId }));
